import { IconButton, Theme } from '@mui/material';
import React from 'react';
import Colors from '../../utils/color';
import {
  HITL7_HEADER_ICON_BOX,
  HITL7_HEADER_ICON_INSET,
} from '../../utils/hitl3Spacing';
import { makeStyles } from '@mui/styles';
import { useAppSelector } from '../../app/hooks';
import { selectIsHitl7Enabled } from '../../redux/features/config/config.selector';

const useStyles = makeStyles((theme: Theme) => ({
  iconButtonClass: {
    width: '32px',
    height: '100%',
    padding: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    margin: '0px !important',
    flexShrink: 0,
  },
  iconComponentClass: {
    width: '100% !important',
    // Smaller box (32x32 inactive, square => no distortion) with tighter
    // padding => smaller icons sitting closer together.
    //
    // HITL-1662: 7.0 tightens these again. The header has to hold one row at
    // 1024px while showing the session id and the restaurant code in FULL —
    // neither may truncate — so every pixel has to come out of the chrome
    // instead. These rules were already dynamic (functions of props), so
    // threading the flag through changes their values without changing the
    // rule count, and the flag-off output is byte-identical.
    //
    // HITL-1702: under 7.0 an ACTIVE icon no longer grows. Every header icon is
    // the same 24px box with a 3px inset (an 18px glyph): the car now shares a
    // two-row block with the speaker and the countdown, which cannot hold its
    // shape if one of its icons swells, and colour already carries the state.
    height: (props: any) => {
      if (props.isHitl7) return `${HITL7_HEADER_ICON_BOX}px !important`;
      return props.isActive ? '42px !important' : '32px !important';
    },
    padding: (props: any) => {
      if (props.isHitl7) return `${HITL7_HEADER_ICON_INSET}px`;
      return props.isActive ? '2px' : '4px';
    },
    borderRadius: '4px',
  },
}));

export interface IVisualIndicator {
  IconComponent: React.ElementType;
  onClick?: (event?: any) => void;
  isActive?: boolean;
  customizedColor?: string;
  id?: string;
  disabled?: boolean;
  'data-testid'?: string;
  'aria-label'?: string;
}

const VisualIndicator = (props: IVisualIndicator) => {
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const { iconButtonClass, iconComponentClass } = useStyles({
    ...props,
    isHitl7,
  });
  const { onClick, IconComponent, id, customizedColor, isActive, disabled } =
    props;

  return (
    <IconButton
      id={id}
      className={iconButtonClass}
      // Inline rather than a makeStyles rule on purpose: adding a rule renumbers
      // every generated class name and moves unrelated snapshots, and making
      // the existing static `width` dynamic would add a second class to every
      // button. Undefined with the flag off, so nothing changes there.
      style={isHitl7 ? { width: '24px' } : undefined}
      onClick={onClick}
      disabled={disabled}
      data-testid={props['data-testid']}
      aria-label={props['aria-label']}
    >
      <IconComponent
        fontSize="small"
        className={iconComponentClass}
        sx={{
          color: customizedColor
            ? customizedColor
            : isActive
            ? Colors.brightGreen
            : Colors.dim,
        }}
      />
    </IconButton>
  );
};

export default VisualIndicator;
